import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Play, Clapperboard, Network, Sparkles, Wrench, ArrowUpRight, Radio, ThumbsUp, ThumbsDown } from "lucide-react";

const PORTALS = [
  { to: "/app/watch", key: "watch", title: "Viewers Dashboard", tag: "WATCH · DISCOVER · PARTICIPATE",
    desc: "Stream films & shows straight from independent creators. Rate, review, drop timed notes and join live watch-parties.",
    icon: Play, span: "lg:col-span-7", live: true },
  { to: "/app/studio", key: "studio", title: "Streamers Workstation", tag: "UPLOAD · PRICE · SELL",
    desc: "Publish your content and sell it directly — pay-per-view, rentals, subscriptions or paid screenings. Own your box office.",
    icon: Clapperboard, span: "lg:col-span-5", live: true, highlight: "Filmmakers can stream films directly to the audience." },
  { to: "/app/network", key: "network", title: "Networking Circuit", tag: "TALENT · STUDIOS · GEAR",
    desc: "Connect makers with producers, studios & investors. Rent equipment and locations. Post what you're looking for.",
    icon: Network, span: "lg:col-span-4", live: true },
  { to: "/app/factory", key: "factory", title: "Influencer Factory", tag: "CRM · PIPELINE · AI",
    desc: "Manage bookings, pipeline, projects, inbox chat and AI-drafted deals. Your creator operations HQ.",
    icon: Sparkles, span: "lg:col-span-4", live: true },
  { to: "/app/tools", key: "tools", title: "ERP & Studio Tools", tag: "BUDGETS · PRE / PRO / POST",
    desc: "Basic filmmaker ERP plus pre-production, production, post and generative creative tooling.",
    icon: Wrench, span: "lg:col-span-4", live: true },
];

function LiveVoteBar() {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { try { setStats(await api.liveStats()); } catch { /* ignore */ } };
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  const vote = async (v) => {
    setBusy(true);
    try { const r = await api.castVote(v); setStats(s => ({ ...s, votes: { yes: r.yes, no: r.no, total: r.yes + r.no }, my_vote: v }));
      toast.success(v === "yes" ? "Thanks — glad you're staying!" : "Thanks for the honesty."); }
    catch { toast.error("Could not record your vote"); } finally { setBusy(false); }
  };

  return (
    <div className="border border-white/10 bg-[#0C0C0C] rounded-xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5" data-testid="live-vote-bar">
      <div className="flex items-center gap-3">
        <span className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-kinetic opacity-60" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-kinetic" />
        </span>
        <div>
          <div className="font-sans text-2xl font-extrabold leading-none" data-testid="live-count">
            {stats ? stats.live.toLocaleString("en-IN") : "—"}
          </div>
          <div className="mono text-white/45 mt-1 flex items-center gap-1"><Radio className="w-3 h-3 text-kinetic" /> CREATORS &amp; VIEWERS LIVE NOW</div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="mono text-white/45 hidden md:inline">KEEP USING INDIEWOOD?</span>
        <button data-testid="vote-yes" onClick={() => vote("yes")} disabled={busy}
          className={`mono px-3 h-9 rounded-sm border inline-flex items-center gap-2 transition ${stats?.my_vote === "yes" ? "border-kinetic text-kinetic bg-kinetic/10" : "border-white/15 text-white/70 hover:border-kinetic hover:text-kinetic"}`}>
          <ThumbsUp className="w-3.5 h-3.5" /> YES {stats ? `· ${stats.votes.yes}` : ""}
        </button>
        <button data-testid="vote-no" onClick={() => vote("no")} disabled={busy}
          className={`mono px-3 h-9 rounded-sm border inline-flex items-center gap-2 transition ${stats?.my_vote === "no" ? "border-coral text-coral bg-white/5" : "border-white/15 text-white/70 hover:border-white/40"}`}>
          <ThumbsDown className="w-3.5 h-3.5" /> NO {stats ? `· ${stats.votes.no}` : ""}
        </button>
      </div>
    </div>
  );
}

export default function PortalHub() {
  const { user } = useAuth();
  return (
    <div className="space-y-8" data-testid="portal-hub">
      <div>
        <div className="mono text-white/40">THE INFRASTRUCTURE FOR INDEPENDENT CULTURE · SAAS</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-3">
          Welcome, {user?.name?.split(" ")[0] || "Maker"}.
        </h1>
        <p className="text-white/60 mt-3 max-w-2xl text-lg">
          One megaplex to <span className="text-kinetic">tell</span> and <span className="text-kinetic">sell</span> stories —
          streaming, networking and creative tools, all in one.
        </p>
        <Link to="/app/pricing" data-testid="hub-pricing-link" className="inline-flex items-center gap-2 mono text-black bg-kinetic px-4 h-9 rounded-sm mt-5 font-bold hover:opacity-90 transition">
          VIEW PLANS &amp; PRICING →
        </Link>
      </div>

      <LiveVoteBar />

      <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-5">
        {PORTALS.map(p => (
          <Link
            key={p.key}
            to={p.to}
            data-testid={`portal-card-${p.key}`}
            className={`group relative overflow-hidden bg-[#121212] border border-white/10 rounded-xl p-7 lg:p-8 transition-all duration-300 hover:border-kinetic/60 hover:-translate-y-0.5 md:col-span-8 ${p.span} min-h-[220px] flex flex-col justify-between`}
          >
            <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-kinetic/5 group-hover:bg-kinetic/10 transition-colors" />
            <div className="relative flex items-start justify-between">
              <div className="w-11 h-11 rounded-sm bg-kinetic/10 border border-kinetic/30 flex items-center justify-center text-kinetic">
                <p.icon className="w-5 h-5" />
              </div>
              {p.live
                ? <ArrowUpRight className="w-5 h-5 text-white/30 group-hover:text-kinetic transition-colors" />
                : <span className="mono text-white/40 border border-white/15 px-2 py-0.5 rounded-sm">SOON</span>}
            </div>
            <div className="relative">
              <div className="mono text-kinetic">{p.tag}</div>
              <h2 className="font-sans text-2xl font-extrabold mt-2 tracking-tight">{p.title}</h2>
              <p className="text-white/55 mt-2 leading-relaxed">{p.desc}</p>
              {p.highlight && (
                <p className="mono text-kinetic mt-3 text-xs border-l-2 border-kinetic pl-3" data-testid="studio-highlight">
                  {p.highlight}
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
